// Translations for Harvest Education Consulting website

export const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      programs: 'Programs',
      ourStory: 'Our Story',
      contact: 'Contact',
    },
    
    // Home Page
    home: {
      heroTitle: 'Hope begins here.',
      heroSubtitle: 'Expert support for ADHD and Reading, rooted in the heart of Amman.',
      bookConsultation: 'Book a Consultation',
      messageWhatsApp: 'Message Us on WhatsApp',
      
      // Early Warning Signs
      signsTitle: 'Does Your Child Show These Signs?',
      signsSubtitle: 'Many children show early signs that they need specialized learning support — and the earlier we help, the faster progress happens.',
      signsDescription: 'At Harvest Education Consulting, we create individualized support plans to help children grow academically, socially, and emotionally — with evidence-based strategies and parent partnership.',
      
      // Warning signs
      limitedSpeech: 'Limited speech or delayed language',
      troubleFocusing: 'Trouble focusing or following instructions',
      difficultyInteracting: 'Difficulty interacting with other children',
      strongEmotions: 'Strong emotions, meltdowns, or sensory sensitivity',
      strugglesReading: 'Struggles with reading, writing, or basic skills',
      
      // Services Section
      servicesTitle: 'Our Programs',
      
      // Empathy Section
      empathyTitle: 'We know the weight you\'re carrying.',
      empathyText: 'The constant advocacy, the worry about the future, and those days when it feels like no one truly "gets" your child. Being a parent is a journey, and when your child learns differently, that journey can feel lonely.',
      empathyQuote: '"Our goal isn\'t just to help your child thrive—it\'s to give you back the joy of simply being their parent."',
      frustrationToFlow: 'From Frustration to Flow',
      frustrationToFlowText: 'Moving past the daily meltdowns and finding the communication tools that bring peace back to your home.',
      worryToWonder: 'From Worry to Wonder',
      worryToWonderText: 'Replacing the fear of what they can\'t do with the excitement of watching them discover what they can.',
      aloneToSupported: 'From Alone to Supported',
      aloneToSupportedText: 'Gaining a team that celebrates every small victory with you like it\'s a giant leap.',
      
      // Philosophy
      philosophyTitle: 'The Harvest Philosophy',
      philosophyText: 'Like a garden, every child needs the right environment and nourishment. We protect the "sprouts" of progress until they are strong enough to harvest their full potential.',
      
      // Family Stories
      story1Title: '"I used to worry about our family gatherings..."',
      story1Text: '"In Amman, family is everything. I used to dread the big Friday lunches because I was always on edge, waiting for a meltdown or a misunderstanding. Since working with Harvest, I\'ve learned how to support my son\'s needs, and he\'s learned how to communicate his. Last Friday, he sat with his cousins and laughed. It was the first time I could just sit and enjoy my coffee."',
      story1Author: '— Maha, Mom of 6-year-old Zaid',
      
      story2Title: '"A burden shared is a burden halved."',
      story2Text: '"We spent years looking for a place that didn\'t just see a \'diagnosis.\' We wanted someone to see our daughter\'s spirit. The team here became our extended family. They didn\'t just teach her; they coached us. The struggle hasn\'t disappeared, but the heaviness has. We finally feel like we have a roadmap."',
      story2Author: '— Omar, Dad of 8-year-old Layla',
      
      // Promise Section
      promiseTitle: 'A Promise to the Families',
      promiseText: 'We respect the traditions and values of our community. Our center is a place of dignity, where every family is treated with the utmost privacy and every child is raised with the ambition they deserve.',
      
      // Approach Section
      approachTitle: 'Our Evidence-Based Approach',
      approachSubtitle: 'We don\'t use one fixed program for every child. Instead, we create individualized learning and therapy plans based on your child\'s strengths, challenges, and developmental needs.',
      progressTitle: 'How We Measure Progress',
      progressSubtitle: 'Parents deserve clarity, not guesswork.',
      progressItem1: 'Clear goals and baseline assessment',
      progressItem2: 'Progress tracking and session notes',
      progressItem3: 'Periodic evaluations and plan updates',
      progressItem4: 'Continuous parent communication',
      
      // Testimonials
      testimonialsTitle: 'What Parents Are Saying',
      testimonialsSubtitle: 'Real stories from families who have seen transformative progress',
      
      // CTA
      ctaTitle: 'Ready to Help Your Child Thrive?',
      ctaText: 'Early intervention makes all the difference. Contact us today for a free consultation and let\'s discuss how we can support your child\'s unique journey.',
      ctaNote: 'saw results in 6 months. Your child can too.',
      scheduleFree: 'Schedule Free Consultation',
    },
    
    // Services/Programs Page
    services: {
      heroTitle: 'Transforming Struggle into Strength',
      heroSubtitle: 'Specialized programs for ADHD, Literacy, and Sensory Development tailored for the families of Amman.',
      explorePrograms: 'Explore Our Programs',
      
      // Service titles
      speechLanguage: 'Speech & Language Development',
      speechLanguageDesc: 'Specialized support for children with limited speech or delayed language development.',
      attentionFocus: 'Attention & Focus Training',
      attentionFocusDesc: 'Evidence-based strategies to help children who struggle with focusing or following instructions.',
      socialSkills: 'Social Skills Development',
      socialSkillsDesc: 'Structured programs to help children interact positively with peers and develop friendships.',
      emotionalRegulation: 'Emotional Regulation Support',
      emotionalRegulationDesc: 'Helping children manage strong emotions, reduce meltdowns, and develop coping strategies.',
      academicSkills: 'Academic Skills Support',
      academicSkillsDesc: 'Individualized support for children struggling with reading, writing, or basic academic skills.',
      sensoryIntegration: 'Sensory Integration Therapy',
      sensoryIntegrationDesc: 'Occupational therapy support for children with sensory processing challenges.',
      
      whatWeAddress: 'What We Address:',
      
      // Evidence-Based Methods
      evidenceTitle: 'Evidence-Based Methods We Use',
      evidenceSubtitle: 'Harvest Education Consulting uses internationally recognized, evidence-based approaches that are widely trusted in special education and developmental support.',
      
      // Progress Section
      progressTitle: 'How We Measure Progress',
      progressSubtitle: 'Parents deserve clarity, not guesswork. That\'s why we ensure every child has:',
      parentPartnership: 'Parent Partnership',
      parentPartnershipText: 'We believe progress is faster when the family is supported too. We work closely with parents through:',
      parentCoaching: 'Parent coaching and guidance',
      homeStrategies: 'Home strategies and routines',
      openFeedback: 'Open feedback and continuous updates',
      partnershipNote: 'Because at Harvest, we support the child and the family — every step of the way.',
      
      // CTA
      ctaTitle: 'Ready to Get Started?',
      ctaText: 'Contact us today to discuss which program is right for your child. We\'re here to answer your questions and guide you every step of the way.',
    },
    
    // About/Our Story Page
    about: {
      heroTitle: 'A place where different is beautiful.',
      storyText: 'We started with a simple belief: that children with learning difficulties don\'t need to be "fixed"—they need to be understood. Our center was built to be a sanctuary for children ages 3–14, providing a blend of clinical expertise and genuine, warm-hearted play.',
      
      // Promises
      ourPromise: 'Our Promise',
      patienceFirst: 'Patience First',
      patienceFirstText: 'We work at your child\'s speed, never ours. Every milestone is celebrated, no matter how small.',
      totalTransparency: 'Total Transparency',
      totalTransparencyText: 'You are part of the team. We share every win and navigate every challenge together.',
      joyfulLearning: 'Joyful Learning',
      joyfulLearningText: 'If a child isn\'t smiling, we haven\'t found the right path yet. Play is our greatest tool.',
      
      // Team
      teamTitle: 'Meet our Care Team',
      founderTitle: 'Founder & Clinical Director',
      specialistsTitle: 'Our Specialists',
      specialistsSubtitle: 'Dedicated to your child\'s growth',
      specialistsDesc: 'Experts in play-based therapy',
      aboutDrTitle: 'About Dr. Najlaa Assaf',
      yearsExperience: 'years experience',
      
      // CTA
      ctaTitle: 'Ready to start your child\'s journey?',
      ctaText: 'Let\'s discuss how we can support your child\'s unique path to growth.',
      bookConsultation: 'Book a consultation →',
      explorePrograms: 'Explore programs →',
    },
    
    // Contact Page
    contact: {
      title: 'Get in Touch',
      subtitle: 'We\'re here to answer your questions and help you take the first step.',
      
      // Contact Info
      visitUs: 'Visit Us',
      callUs: 'Call Us',
      emailUs: 'Email Us',
      workingHours: 'Working Hours',
      
      // Form
      formTitle: 'Send us a Message',
      yourName: 'Your Name',
      yourEmail: 'Your Email',
      phone: 'Phone Number',
      subject: 'Subject',
      message: 'Your Message',
      sendMessage: 'Send Message',
      
      // WhatsApp
      whatsappTitle: 'Prefer WhatsApp?',
      whatsappText: 'Send us a message directly on WhatsApp for a quick response.',
      messageOnWhatsApp: 'Message on WhatsApp',
    },
    
    // Footer
    footer: {
      copyright: '© 2026 Harvest Education Consulting. All rights reserved.',
    },
    
    // Common
    common: {
      learnMore: 'Learn More',
      bookNow: 'Book Now',
      getStarted: 'Get Started',
    },

    // Approaches
    approaches: {
      iep: 'Individualized Education Plans (IEP-Style Programming)',
      iepDesc: 'Each child follows a personalized plan with clear goals, measurable progress, and regular updates - so parents can see real improvement over time.',
      aba: 'Applied Behavior Strategies (ABA-Informed Support)',
      abaDesc: 'We use behavior-based strategies to support attention, learning readiness, positive behavior development, and communication - always in a respectful, child-centered way.',
      speech: 'Speech & Language Development Strategies',
      speechDesc: 'Structured techniques for language-building routines, articulation practice, and social communication training.',
      ot: 'Occupational Therapy & Sensory Integration',
      otDesc: 'Programs designed to improve sensory regulation, motor coordination, handwriting readiness, and daily living skills.',
      social: 'Social Skills & Emotional Regulation',
      socialDesc: 'Helping children develop turn-taking, peer interaction, emotional expression, and coping strategies for school readiness.',
    },

    // Testimonials
    testimonials: {
      t1: 'When we first came to Harvest, our son barely spoke. Within 6 months, he was forming sentences and actually wanting to communicate. Dr. Najlaa and her team gave us our son back. We are forever grateful.',
      t2: 'Our daughter had severe meltdowns every day. The team at Harvest taught her emotional regulation skills that transformed our home life. She is now thriving in preschool and making friends.',
      t3: 'After struggling in school for two years, Harvest individualized approach helped my son finally understand reading. His confidence has soared, and he actually enjoys learning now.',
      t4: 'The progress reports and parent coaching at Harvest made us active partners in our daughter growth. We learned strategies we could use at home, which accelerated her development tremendously.',
    },

    // Service Details (bullet points)
    serviceDetails: {
      speech: ['Language-building routines', 'Articulation and pronunciation practice', 'Expressive and receptive language development', 'Social communication training'],
      attention: ['Attention span building', 'Following multi-step directions', 'Learning readiness skills', 'Classroom behavior preparation'],
      social: ['Turn-taking and sharing', 'Peer interaction skills', 'Play and cooperation', 'Building confidence in social settings'],
      emotional: ['Emotional expression techniques', 'Self-regulation strategies', 'Coping mechanisms', 'Sensory sensitivity support'],
      academic: ['Reading readiness and literacy', 'Writing and fine motor skills', 'Early numeracy concepts', 'School readiness preparation'],
      sensory: ['Sensory regulation techniques', 'Motor coordination development', 'Handwriting readiness', 'Daily living skills'],
    },

    // Dr. Najlaa
    drNajlaa: {
      name: 'Dr. Najlaa Assaf',
      credentials: 'PhD in Special Education, The University of Jordan',
      experience: '20 years',
      bio: 'Dr. Najlaa Assaf is a passionate advocate for children with special learning needs. With over 20 years of experience and a PhD in Special Education from The University of Jordan, she has dedicated her career to helping children ages 3-14 overcome developmental challenges and reach their full potential. Her evidence-based approach combines compassion with proven methodologies to create lasting positive change in the lives of children and their families.',
    },
  },
  
  ar: {
    // Navigation
    nav: {
      home: 'الرئيسية',
      programs: 'البرامج',
      ourStory: 'قصتنا',
      contact: 'تواصل معنا',
    },
    
    // Home Page
    home: {
      heroTitle: 'الأمل يبدأ من هنا.',
      heroSubtitle: 'دعم متخصص لفرط الحركة وتشتت الانتباه والقراءة، من قلب عمّان.',
      bookConsultation: 'احجز استشارة',
      messageWhatsApp: 'راسلنا على واتساب',
      
      // Early Warning Signs
      signsTitle: 'هل يُظهر طفلك هذه العلامات؟',
      signsSubtitle: 'يُظهر العديد من الأطفال علامات مبكرة تدل على حاجتهم لدعم تعليمي متخصص — وكلما ساعدنا مبكراً، كان التقدم أسرع.',
      signsDescription: 'في هارفست لاستشارات التعليم، نضع خططاً فردية لمساعدة الأطفال على النمو أكاديمياً واجتماعياً وعاطفياً — باستخدام استراتيجيات مبنية على الأدلة العلمية وبالشراكة مع الأهل.',
      
      // Warning signs
      limitedSpeech: 'محدودية الكلام أو تأخر اللغة',
      troubleFocusing: 'صعوبة في التركيز أو اتباع التعليمات',
      difficultyInteracting: 'صعوبة في التفاعل مع الأطفال الآخرين',
      strongEmotions: 'مشاعر قوية أو نوبات غضب أو حساسية حسية',
      strugglesReading: 'صعوبات في القراءة والكتابة والمهارات الأساسية',
      
      // Services Section
      servicesTitle: 'برامجنا',
      
      // Empathy Section
      empathyTitle: 'المناصرة مستمرة',
      empathyText: 'إن القلق على المستقبل و تلك الأيام التي تشعرون فيها أن لا أحد يفهم طفلكم قد تشعركم أنكم كأب أو أم تسيرون في هذه الرحلة لوحدكم. لكن عندما نساعد أطفالكم على التعلم بطرق مختلفة و متنوعة ستبدو الرحلة أكثر راحة.',
      empathyQuote: '"هدفنا ليس فقط مساعدة طفلك على الازدهار — بل أن نعيد لكم فرحة أن تكونوا ببساطة والديه."',
      frustrationToFlow: 'من الإحباط إلى الانسجام',
      frustrationToFlowText: 'تجاوز نوبات الغضب اليومية وإيجاد أدوات التواصل التي تعيد السلام إلى منزلكم.',
      worryToWonder: 'من القلق إلى الدهشة',
      worryToWonderText: 'استبدال مشاعر الخوف من الفشل إلى دافع إيجابي، عبر تمكين الأطفال من اكتشاف إمكاناتهم وبناء ثقتهم بأنفسهم',
      aloneToSupported: 'من الوحدة إلى الدعم',
      aloneToSupportedText: 'اكتساب فريق يحتفل معكم بكل انتصار صغير كأنه قفزة عملاقة.',
      
      // Philosophy
      philosophyTitle: 'فلسفة هارفست',
      philosophyText: 'مثل الحديقة، كل طفل يحتاج البيئة المناسبة والرعاية. نحمي "براعم" التقدم حتى تصبح قوية بما يكفي لحصاد إمكاناتهم الكاملة.',
      
      // Family Stories
      story1Title: '"كنت أقلق من تجمعاتنا العائلية..."',
      story1Text: '"في عمّان، العائلة هي كل شيء. كنت أخشى غداء الجمعة الكبير لأنني كنت دائماً متوترة، أنتظر نوبة غضب أو سوء فهم. منذ العمل مع هارفست، تعلمت كيف أدعم احتياجات ابني، وهو تعلم كيف يعبر عنها. الجمعة الماضية، جلس مع أبناء عمومته وضحك. كانت المرة الأولى التي أستطيع فيها الجلوس والاستمتاع بقهوتي."',
      story1Author: '— مها، أم زيد (6 سنوات)',
      
      story2Title: '"الهم المشترك نصف هم."',
      story2Text: '"أمضينا سنوات نبحث عن مكان لا يرى فقط \'التشخيص\'. أردنا من يرى روح ابنتنا. أصبح الفريق هنا عائلتنا الممتدة. لم يعلموها فقط؛ بل درّبونا. لم يختفِ الصراع، لكن الثقل اختفى. أخيراً نشعر أن لدينا خارطة طريق."',
      story2Author: '— عمر، أب ليلى (8 سنوات)',
      
      // Promise Section
      promiseTitle: 'وعد للعائلات',
      promiseText: 'نحترم تقاليد و قيم مجتمعنا. مركزنا مكان للكرامة، حيث تُعامل كل عائلة بأقصى درجات الخصوصية، ونطمح لوصول الطفل لما يستحقه.',
      
      // Approach Section
      approachTitle: 'نهجنا المبني على الأدلة',
      approachSubtitle: 'لا نستخدم برنامجاً واحداً ثابتاً لكل الأطفال. بدلاً من ذلك، نضع خطط تعلم وعلاج فردية بناءً على نقاط قوة طفلك وتحدياته واحتياجاته التنموية.',
      progressTitle: 'كيف نقيس التقدم',
      progressSubtitle: 'الأهل يستحقون الوضوح، لا التخمين.',
      progressItem1: 'أهداف واضحة وتقييم أساسي',
      progressItem2: 'تتبع التقدم وملاحظات الجلسات',
      progressItem3: 'تقييمات دورية وتحديث الخطط',
      progressItem4: 'تواصل مستمر مع الأهل',
      
      // Testimonials
      testimonialsTitle: 'ماذا يقول الأهل',
      testimonialsSubtitle: 'قصص حقيقية من عائلات شهدت تقدماً تحويلياً',
      
      // CTA
      ctaTitle: 'مستعدون لمساعدة طفلكم على الازدهار؟',
      ctaText: 'التدخل المبكر يصنع كل الفرق. تواصلوا معنا اليوم للحصول على استشارة مجانية ولنناقش كيف يمكننا دعم رحلة طفلكم الفريدة.',
      ctaNote: 'رأى نتائج في 6 أشهر. طفلكم يمكنه ذلك أيضاً.',
      scheduleFree: 'احجز استشارة مجانية',
    },
    
    // Services/Programs Page
    services: {
      heroTitle: 'تحويل الصراع إلى قوة',
      heroSubtitle: 'برامج متخصصة لفرط الحركة والقراءة والتطور الحسي مصممة لعائلات عمّان.',
      explorePrograms: 'استكشف برامجنا',
      
      // Service titles
      speechLanguage: 'تطوير النطق واللغة',
      speechLanguageDesc: 'دعم متخصص للأطفال ذوي الكلام المحدود أو تأخر تطور اللغة.',
      attentionFocus: 'تدريب الانتباه والتركيز',
      attentionFocusDesc: 'استراتيجيات مبنية على الأدلة العلمية لمساعدة الأطفال الذين يعانون من صعوبة في التركيز أو اتباع التعليمات.',
      socialSkills: 'تطوير المهارات الاجتماعية',
      socialSkillsDesc: 'برامج منظمة لمساعدة الأطفال على التفاعل الإيجابي مع أقرانهم وتكوين صداقات.',
      emotionalRegulation: 'دعم التنظيم العاطفي',
      emotionalRegulationDesc: 'مساعدة الأطفال على إدارة المشاعر القوية وتقليل نوبات الغضب وتطوير استراتيجيات التكيف.',
      academicSkills: 'دعم المهارات الأكاديمية',
      academicSkillsDesc: 'دعم فردي للأطفال الذين يعانون من القراءة والكتابة أو المهارات الأكاديمية الأساسية.',
      sensoryIntegration: 'العلاج بالتكامل الحسي',
      sensoryIntegrationDesc: 'دعم العلاج الوظيفي للأطفال الذين يعانون من تحديات المعالجة الحسية.',
      
      whatWeAddress: 'ما نعالجه:',
      
      // Evidence-Based Methods
      evidenceTitle: 'الأساليب المبنية على الأدلة العلمية التي نستخدمها',
      evidenceSubtitle: 'تستخدم هارفست لاستشارات التعليم أساليب معترف بها دولياً ومبنية على الأدلة العلمية وموثوقة على نطاق واسع في التربية الخاصة والدعم التنموي.',
      
      // Progress Section
      progressTitle: 'كيف نقيس التقدم',
      progressSubtitle: 'الأهل يستحقون الوضوح، لا التخمين. لذلك نضمن أن كل طفل لديه:',
      parentPartnership: 'الشراكة مع الأهل',
      parentPartnershipText: 'نؤمن أن التقدم أسرع عندما تكون العائلة مدعومة أيضاً. نعمل عن قرب مع الأهل من خلال:',
      parentCoaching: 'تدريب وتوجيه الأهل',
      homeStrategies: 'استراتيجيات وروتين منزلي',
      openFeedback: 'تغذية راجعة مفتوحة وتحديثات مستمرة',
      partnershipNote: 'لأننا في هارفست، ندعم الطفل والعائلة — في كل خطوة على الطريق.',
      
      // CTA
      ctaTitle: 'مستعدون للبدء؟',
      ctaText: 'تواصلوا معنا اليوم لمناقشة البرنامج المناسب لطفلكم. نحن هنا للإجابة على أسئلتكم وإرشادكم في كل خطوة.',
    },
    
    // About/Our Story Page
    about: {
      heroTitle: 'مكان حيث الاختلاف جميل.',
      storyText: 'بدأنا بإيمان بسيط: أن الأطفال ذوي صعوبات التعلم لا يحتاجون إلى "إصلاح" — يحتاجون إلى الفهم. بني مركزنا ليكون بيئة داعمة للأطفال من 3 إلى 14 سنة، يوفر لهم الرعاية المتكاملة تجمع بين الخبرة الميدانية واللعب الهادف مما يسهم في نموهم النفسي والتربوي السليم.',
      
      // Promises
      ourPromise: 'وعدنا',
      patienceFirst: 'الصبر أولاً',
      patienceFirstText: 'نعمل بسرعة طفلكم، ليس سرعتنا. نحتفل بكل إنجاز، مهما كان صغيراً.',
      totalTransparency: 'الشفافية الكاملة',
      totalTransparencyText: 'أنتم جزء من الفريق. نشارككم كل نجاح ونتجاوز كل تحدٍ معاً.',
      joyfulLearning: 'التعلم المرح',
      joyfulLearningText: 'إذا لم يكن الطفل يبتسم، فلم نجد الطريق الصحيح بعد. اللعب هو أعظم أدواتنا.',
      
      // Team
      teamTitle: 'تعرفوا على فريق الرعاية',
      founderTitle: 'المؤسس والمدير التنفيذي',
      specialistsTitle: 'أخصائيونا',
      specialistsSubtitle: 'ملتزمون بنمو طفلكم',
      specialistsDesc: 'خبراء في العلاج باللعب',
      aboutDrTitle: 'عن د. نجلاء عساف',
      yearsExperience: 'سنوات خبرة',
      
      // CTA
      ctaTitle: 'مستعدون لبدء رحلة طفلكم؟',
      ctaText: 'لنناقش كيف يمكننا دعم مسار نمو طفلكم الفريد.',
      bookConsultation: 'احجز استشارة ←',
      explorePrograms: 'استكشف البرامج ←',
    },
    
    // Contact Page
    contact: {
      title: 'تواصلوا معنا',
      subtitle: 'نحن هنا للإجابة على أسئلتكم ومساعدتكم في اتخاذ الخطوة الأولى.',
      
      // Contact Info
      visitUs: 'زورونا',
      callUs: 'اتصلوا بنا',
      emailUs: 'راسلونا',
      workingHours: 'ساعات العمل',
      
      // Form
      formTitle: 'أرسلوا لنا رسالة',
      yourName: 'الاسم',
      yourEmail: 'البريد الإلكتروني',
      phone: 'رقم الهاتف',
      subject: 'الموضوع',
      message: 'رسالتكم',
      sendMessage: 'إرسال الرسالة',
      
      // WhatsApp
      whatsappTitle: 'تفضلون واتساب؟',
      whatsappText: 'أرسلوا لنا رسالة مباشرة على واتساب للرد السريع.',
      messageOnWhatsApp: 'راسلونا على واتساب',
    },
    
    // Footer
    footer: {
      copyright: '© 2026 هارفست لاستشارات التعليم. جميع الحقوق محفوظة.',
    },
    
    // Common
    common: {
      learnMore: 'اعرف المزيد',
      bookNow: 'احجز الآن',
      getStarted: 'ابدأ الآن',
    },

    // Approaches
    approaches: {
      iep: 'خطط التعليم الفردية (برمجة على طريقة IEP)',
      iepDesc: 'يتبع كل طفل خطة مخصصة بأهداف واضحة وتقدم قابل للقياس وتحديثات منتظمة - حتى يتمكن الأهل من رؤية تحسن حقيقي مع الوقت.',
      aba: 'استراتيجيات السلوك التطبيقي (دعم مستنير من ABA)',
      abaDesc: 'نستخدم استراتيجيات مبنية على السلوك لدعم الانتباه والاستعداد للتعلم وتطوير السلوك الإيجابي والتواصل - دائماً بطريقة محترمة ومتمحورة حول الطفل.',
      speech: 'استراتيجيات تطوير النطق واللغة',
      speechDesc: 'تقنيات منظمة لروتينات بناء اللغة وممارسة النطق وتدريب التواصل الاجتماعي.',
      ot: 'العلاج الوظيفي والتكامل الحسي',
      otDesc: 'برامج مصممة لتحسين التنظيم الحسي والتنسيق الحركي والاستعداد للكتابة ومهارات الحياة اليومية.',
      social: 'المهارات الاجتماعية والتنظيم العاطفي',
      socialDesc: 'مساعدة الأطفال على تطوير التناوب والتفاعل مع الأقران والتعبير العاطفي واستراتيجيات التكيف للاستعداد المدرسي.',
    },

    // Testimonials
    testimonials: {
      t1: 'عندما جئنا لأول مرة إلى هارفست، كان ابننا بالكاد يتكلم. في غضون 6 أشهر، أصبح يكوّن جملاً ويريد فعلاً التواصل. د. نجلاء وفريقها أعادوا لنا ابننا. نحن ممتنون للأبد.',
      t2: 'كانت ابنتنا تعاني من نوبات غضب شديدة كل يوم. فريق هارفست علّمها مهارات التنظيم العاطفي التي غيّرت حياتنا المنزلية. هي الآن تزدهر في الروضة وتكوّن صداقات.',
      t3: 'بعد معاناة في المدرسة لسنتين، ساعد نهج هارفست الفردي ابني على فهم القراءة أخيراً. ارتفعت ثقته بنفسه، وهو الآن يستمتع فعلاً بالتعلم.',
      t4: 'تقارير التقدم وتدريب الأهل في هارفست جعلتنا شركاء فاعلين في نمو ابنتنا. تعلمنا استراتيجيات يمكننا استخدامها في المنزل، مما سرّع تطورها بشكل هائل.',
    },

    // Service Details (bullet points)
    serviceDetails: {
      speech: ['روتينات بناء اللغة', 'ممارسة النطق والتلفظ', 'تطوير اللغة التعبيرية والاستقبالية', 'تدريب التواصل الاجتماعي'],
      attention: ['بناء مدى الانتباه', 'اتباع التعليمات متعددة الخطوات', 'مهارات الاستعداد للتعلم', 'التحضير لسلوك الفصل الدراسي'],
      social: ['التناوب والمشاركة', 'مهارات التفاعل مع الأقران', 'اللعب والتعاون', 'بناء الثقة في البيئات الاجتماعية'],
      emotional: ['تقنيات التعبير العاطفي', 'استراتيجيات التنظيم الذاتي', 'آليات التكيف', 'دعم الحساسية الحسية'],
      academic: ['الاستعداد للقراءة ومحو الأمية', 'الكتابة والمهارات الحركية الدقيقة', 'مفاهيم الحساب المبكرة', 'التحضير للاستعداد المدرسي'],
      sensory: ['تقنيات التنظيم الحسي', 'تطوير التنسيق الحركي', 'الاستعداد للكتابة اليدوية', 'مهارات الحياة اليومية'],
    },

    // Dr. Najlaa
    drNajlaa: {
      name: 'د. نجلاء عساف',
      credentials: 'حاصلة على درجة الدكتوراه في التربية الخاصة من الجامعة الأردنية.',
      experience: '20',
      bio: 'خبرة ميدانية منذ 20 عام.\nكرّست حياتها المهنية لمساعدة الأطفال من عمر 3-14 عام للتغلب على التحديات التنموية والوصل بهم إلى إمكانياتهم الكاملة.\nيجمع نهجها المبني على الأدلة المثبتة علمياً لإحداث تغيير إيجابي دائم في حياة الأطفال و عائلاتهم',
    },
  },
};
